import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Database,
  Trash2,
  Loader2,
  Check,
  Users,
  Shield,
  MapPin,
  FileText,
  Stethoscope,
  UserCircle,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const SeedSection = ({ t }) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [seedResult, setSeedResult] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/seed/preview`);
      setPreviewData(response.data);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Error fetching seed preview');
    } finally {
      setLoading(false);
    }
  };

  const executeSeed = async () => {
    if (confirmText !== 'SEED') {
      toast.error('Please type SEED to confirm');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/seed/execute`);
      setSeedResult(response.data);
      setShowConfirmDialog(false);
      setConfirmText('');
      toast.success('Platform seeded successfully!');
    } catch (error) {
      console.error('Error executing seed:', error);
      toast.error(error.response?.data?.detail || 'Error executing seed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Warning Card */}
      <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="h-5 w-5" />
            Database Seed - Danger Zone
          </CardTitle>
          <CardDescription className="text-red-600 dark:text-red-400">
            This operation will <strong>ERASE ALL DATA</strong> in the platform and seed it with default information.
            This action cannot be undone!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border">
              <h4 className="font-medium mb-3">What will be seeded:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <UserCircle className="h-4 w-4 text-blue-500" />
                  <span><strong>Admin User:</strong> admin@especialistasencasa.com (same password: Admin123!)</span>
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-500" />
                  <span><strong>Roles:</strong> Admin, Coordinator, Operator, Specialist, Viewer</span>
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-500" />
                  <span><strong>Permissions:</strong> Full permission set for all modules</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-orange-500" />
                  <span><strong>Countries:</strong> Colombia only</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-teal-500" />
                  <span><strong>Cities:</strong> 125 municipalities of Antioquia</span>
                </li>
                <li className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-pink-500" />
                  <span><strong>Patient Custom Fields:</strong> ~90 fields organized in 13 groups</span>
                </li>
                <li className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-indigo-500" />
                  <span><strong>Specialist Custom Fields:</strong> ~50 fields organized in 6 groups</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span><strong>Workflows & Queues:</strong> Empty (ready for configuration)</span>
                </li>
              </ul>
            </div>

            <Separator />

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={fetchPreview}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Database className="h-4 w-4 mr-2" />
                )}
                Preview Seed Data
              </Button>

              <Button
                variant="destructive"
                onClick={() => setShowConfirmDialog(true)}
                disabled={loading}
                data-testid="execute-seed-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Erase & Seed Platform
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Data */}
      {previewData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Seed Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96">
              <div className="space-y-4">
                {/* Admin User */}
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <UserCircle className="h-4 w-4" />
                    Admin User
                  </h4>
                  <p className="text-sm">
                    {previewData.will_create.admin_user.email} ({previewData.will_create.admin_user.name})
                  </p>
                </div>

                {/* Roles */}
                <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4" />
                    Roles ({previewData.will_create.roles.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {previewData.will_create.roles.map(role => (
                      <Badge key={role.id} variant="outline">{role.name}</Badge>
                    ))}
                  </div>
                </div>

                {/* Permissions */}
                <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4" />
                    Permissions
                  </h4>
                  <p className="text-sm">{previewData.will_create.permissions} permissions across all modules</p>
                </div>

                {/* Cities */}
                <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4" />
                    Cities - Antioquia ({previewData.will_create.cities_count})
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {previewData.will_create.cities_sample.map((city, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{city}</Badge>
                    ))}
                  </div>
                </div>

                {/* Patient Custom Fields */}
                <div className="p-3 bg-pink-50 dark:bg-pink-950/20 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4" />
                    Patient Custom Fields ({previewData.will_create.patient_custom_fields_count})
                  </h4>
                  <p className="text-sm mb-2">Groups:</p>
                  <div className="flex flex-wrap gap-1">
                    {previewData.will_create.patient_custom_field_groups.map((group, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{group}</Badge>
                    ))}
                  </div>
                </div>

                {/* Specialist Custom Fields */}
                <div className="p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Stethoscope className="h-4 w-4" />
                    Specialist Custom Fields ({previewData.will_create.specialist_custom_fields_count})
                  </h4>
                  <p className="text-sm mb-2">Groups:</p>
                  <div className="flex flex-wrap gap-1">
                    {previewData.will_create.specialist_custom_field_groups.map((group, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{group}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Seed Result */}
      {seedResult && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <Check className="h-5 w-5" />
              Seed Completed Successfully
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{seedResult.summary.users}</div>
                <div className="text-sm text-gray-500">Users</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{seedResult.summary.roles}</div>
                <div className="text-sm text-gray-500">Roles</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{seedResult.summary.permissions}</div>
                <div className="text-sm text-gray-500">Permissions</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{seedResult.summary.cities}</div>
                <div className="text-sm text-gray-500">Cities</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-pink-600">{seedResult.summary.patient_custom_field_groups}</div>
                <div className="text-sm text-gray-500">Patient Groups</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-pink-600">{seedResult.summary.patient_custom_fields}</div>
                <div className="text-sm text-gray-500">Patient Fields</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">{seedResult.summary.specialist_custom_field_groups}</div>
                <div className="text-sm text-gray-500">Specialist Groups</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">{seedResult.summary.specialist_custom_fields}</div>
                <div className="text-sm text-gray-500">Specialist Fields</div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                <strong>Note:</strong> You may need to log out and log back in to refresh your session with the new data.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Platform Seed
            </DialogTitle>
            <DialogDescription>
              This will <strong>permanently delete ALL data</strong> including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All users (except the new admin)</li>
                <li>All patients</li>
                <li>All specialists</li>
                <li>All orders</li>
                <li>All medications and services</li>
                <li>All workflows and configurations</li>
                <li>All audit logs</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-seed">
                Type <strong>SEED</strong> to confirm this action:
              </Label>
              <Input
                id="confirm-seed"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="Type SEED"
                className="font-mono"
                data-testid="confirm-seed-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                setConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeSeed}
              disabled={confirmText !== 'SEED' || loading}
              data-testid="confirm-seed-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Erase & Seed Platform
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeedSection;
